import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Box, DialogContent, DialogTitle, Divider, IconButton } from '@mui/material';
import classnames from 'classnames';

import { useEventEmitter } from '@ezbobdev/ui-commons';
import { MainContext, SYSTEM_EVENTS, useTranslation } from '@ezbobdev/ui-framework';

import { css, styled } from '../../Utils/style';
import MaterialTypography from '../MaterialTypography/MaterialTypography';
import Dialog from './MaterialDialog.styled';

const MaterialDialog = (props) => {
  const {
    children,
    className,
    fullScreen = false,
    fullWidth,
    open,
    dialogTitle,
    dialogActions,
    withDivider = false,
    shouldAutoFocusCloseButton = true,
    ...rest
  } = props;
  const [dialogContentRef, setDialogContentRef] = useState(null);
  const { emitEvent } = useEventEmitter();

  const { actions } = useContext(MainContext);

  const forceScrollbar = actions?.settings?.getUserConfigProp('widgetsConfig.globals.MaterialDialog.forceScrollbar');

  // handling backoffice
  let stepName;
  let pageDetails;

  try {
    stepName = actions?.userState?.getCurrentStepName();
    pageDetails = actions?.userState?.getCurrentPageDetails();
  } catch (error) {
    console.log(error);
  }

  const handleDialogEntered = () => {
    const muiDialogTitle = dialogContentRef
      ?.querySelector('.MuiDialogTitle-root')
      ?.querySelector('.MuiTypography-root')?.textContent;

    const iframeTitle = dialogContentRef?.querySelector('.MuiTypography-root.MuiDialogTitle-root')?.textContent;

    emitEvent(SYSTEM_EVENTS.POPUP_OPEN, {
      dialogTitle: muiDialogTitle || iframeTitle || '',
      page: stepName || pageDetails?.pageName,
    });

    if (shouldAutoFocusCloseButton) {
      dialogContentRef?.querySelector('.dialog-close-icon')?.focus();
    }

    if (!props.disablePortal) {
      document.getElementById('root').setAttribute('inert', 'true');
    }
  };

  const handleDialogExit = () => {
    document.getElementById('root')?.removeAttribute('inert');
  };

  useEffect(() => {
    return () => {
      document.getElementById('root')?.removeAttribute('inert');
    };
  }, []);

  return useMemo(() => {
    return (
      <Dialog
        className={classnames(MaterialDialog.displayName, className, forceScrollbar && 'force-scrollbar')}
        data-testid={MaterialDialog.displayName}
        fullScreen={fullScreen}
        fullWidth={fullWidth}
        open={open}
        TransitionProps={{ onEntered: handleDialogEntered, onExit: handleDialogExit }}
        aria-modal={true}
        {...rest}
      >
        {dialogTitle}
        <DialogContent tabIndex={-1} ref={setDialogContentRef}>
          {children}
        </DialogContent>

        {withDivider && !!children && <Divider />}

        {dialogActions}
      </Dialog>
    );
  }, [open, className, children, rest]);
};

MaterialDialog.displayName = 'MaterialDialog';

MaterialDialog.propTypes = {
  children: PropTypes.node.isRequired,
  fullScreen: PropTypes.bool,
};

export const MaterialDialogTitle = (props) => {
  const { t } = useTranslation();
  const { emitEvent } = useEventEmitter();

  const { children, onClose, variant = 'h6', disableTypography, withDivider = true, icon = null } = props;

  const TitleComponent = disableTypography ? Box : DialogTitle;

  return (
    <TitleComponent className="MuiDialogTitle-root">
      {onClose && (
        <IconButton
          className="dialog-close-icon"
          aria-label={t(`globals.popupClose`)}
          onClick={(e) => {
            emitEvent(e.type, { label: t(`globals.popupClose`) });
            onClose();
          }}
          data-testid="close-icon-button"
        >
          <Close />
        </IconButton>
      )}
      {icon}
      {disableTypography ? children : <MaterialTypography variant={variant}>{children}</MaterialTypography>}
      {withDivider && !!children && <Divider />}
    </TitleComponent>
  );
};

MaterialDialogTitle.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf([
    'body1',
    'body2',
    'button',
    'caption',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'inherit',
    'overline',
    'subtitle1',
    'subtitle2',
  ]),
};

const IFrameDialogWrapper = styled(MaterialDialog)`
  ${({ theme }) => css`
    iframe {
      flex: 1;
      margin-top: ${theme.spacing(3)};
    }
  `}
`;

export const MaterialIFrameDialog = (props) => {
  const { t } = useTranslation();

  const { onClose, src, title, ...materialDialogProps } = props;

  return (
    <IFrameDialogWrapper onClose={onClose} variant="large" {...materialDialogProps}>
      <IconButton
        className="dialog-close-icon"
        aria-label={t(`globals.popupClose`)}
        onClick={onClose}
        data-testid="close-icon-button"
      >
        <Close />
      </IconButton>

      {title && <DialogTitle variant="h2">{title}</DialogTitle>}

      <iframe src={src} title={title} />
    </IFrameDialogWrapper>
  );
};

export { MaterialDialogActions, MaterialDialogContent } from './MaterialDialog.styled';
export default React.memo(MaterialDialog);
